import { AxiosResponse } from "axios";
import { appConstants } from "../config/app-constants";
import InitApi from "./init";

// Interfaces for Blog API
export interface IBlogData {
  title: string;
  slug: string;
  content: string;
  summary?: string;
  featuredImage?: string;
  status: "draft" | "published";
  tags?: string[];
  categories?: string[];
  author?: string;
}

export interface IBlogResponse {
  id?: number;
  slug?: string;
  title?: string;
  category_name?: string;
  image_url?: string;
  content?: string;
  created_by?: string;
  updated_by?: string;
  created_at?: string;
  updated_at?: string;
  meta_title?: string;
  meta_description?: string;
  keywords?: string;
  author_profile_link?: string;
  author_bio?: string;
  read_time?: string;
  tldr_summary?: string;
  status: number;
  views?: number;
}

export interface IFaqData {
  question: string;
  answer: string;
  status?: string;
  category?: string;
}

export interface IFaqResponse {
  id: string;
  question: string;
  answer: string;
  status: string;
  category?: string;
  createdAt: string;
  updatedAt: string;
}

export interface IBlogParams {
  search?: string;
  offset?: number;
  count?: number;
  orderBy?: string;
  order?: "asc" | "desc";
  category?: string;
  tag?: string;
  status?: "draft" | "published";
}

class BlogApi {
  static _instance: BlogApi;

  static getInstance = () => {
    if (this._instance) {
      return this._instance;
    }
    this._instance = new BlogApi();
    return this._instance;
  };

  _apiSvc;

  constructor() {
    this._apiSvc = InitApi.getInstance();
  }

  // Get list of blogs with pagination and filtering
  getBlogs = async (
    params: IBlogParams
  ): Promise<{ count: number; records: IBlogResponse[] }> => {
    const {
      search,
      offset = 0,
      count = 10,
      orderBy,
      order,
      category,
      tag,
      status,
    } = params;
    let url = `blogs/list?offset=${offset}`;

    if (search) {
      url += `&search=${search}`;
    }
    if (count) {
      url += `&count=${count}`;
    }
    if (orderBy) {
      url += `&orderBy=${orderBy}`;
    }
    if (order) {
      url += `&order=${order}`;
    }
    if (category) {
      url += `&category=${category}`;
    }
    if (tag) {
      url += `&tag=${tag}`;
    }
    if (status) {
      url += `&status=${status}`;
    }

    const response = await this._apiSvc.get(`${appConstants.apiUrl}/${url}`);
    return response.data;
  };

  // Get a single blog by slug
  getBlogBySlug = async (slug: string): Promise<IBlogResponse> => {
    const response = await this._apiSvc.get(
      `${appConstants.apiUrl}/blogs/${slug}`
    );
    return response.data;
  };

  // Get a single blog by ID
  getBlogById = async (id: string): Promise<IBlogResponse> => {
    const response = await this._apiSvc.get(
      `${appConstants.apiUrl}/blogs/single/${id}`
    );
    return response.data;
  };

  // Create a new blog
  createBlog = async (data: IBlogData): Promise<IBlogResponse> => {
    const response = await this._apiSvc.post(
      `${appConstants.apiUrl}/blogs/create`,
      data
    );
    return response.data;
  };

  // Update an existing blog
  updateBlog = async (id: string, data: IBlogData): Promise<IBlogResponse> => {

    const response = await this._apiSvc.put(
      `${appConstants.apiUrl}/blogs/update/${id}`,
      data
    );
    return response.data;
  };

  // Delete a blog
  deleteBlog = async (id: string): Promise<AxiosResponse> => {
    return this._apiSvc.delete(`${appConstants.apiUrl}/blogs/delete/${id}`);
  };

  // Upload a blog image
  uploadBlogImage = async (file: File): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append("image", file);

    const response = await this._apiSvc.post(
      `${appConstants.apiUrl}/blogs/upload-blog-image`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data;
  };

  // Change blog status (publish/unpublish)
  updateBlogStatus = async (
    id: string,
    status: number,
    updated_by:string
  ): Promise<AxiosResponse> => {
    return this._apiSvc.put(`${appConstants.apiUrl}/blogs/publish/${id}`, {
      status,
      updated_by
    });
  };

  // Get blog categories
  getBlogCategories = async (): Promise<string[]> => {
    const response = await this._apiSvc.get(
      `${appConstants.apiUrl}/blog-categories`
    );
    return response.data;
  };

  // Get blog stats
  getBlogStats = async (): Promise<any> => {
    const response = await this._apiSvc.get(
      `${appConstants.apiUrl}/blogs/stats`
    );
    return response.data;
  };

  // FAQ related endpoints
  getFaqs = async (
    page: number,
    limit: number,
    status?: string
  ): Promise<any> => {
    let url = `faqs?page=${page}&limit=${limit}`;
    if (status) {
      url += `&status=${status}`;
    }
    const response = await this._apiSvc.get(`${appConstants.apiUrl}/${url}`);
    return response.data;
  };

  createFaq = async (data: IFaqData): Promise<IFaqResponse> => {
    const response = await this._apiSvc.post(
      `${appConstants.apiUrl}/faqs/create`,
      data
    );
    return response.data;
  };

  updateFaq = async (id: string, data: IFaqData): Promise<IFaqResponse> => {
    const response = await this._apiSvc.put(
      `${appConstants.apiUrl}/faqs/${id}`,
      data
    );
    return response.data;
  };

  deleteFaq = async (id: string): Promise<AxiosResponse> => {
    return this._apiSvc.delete(`${appConstants.apiUrl}/faqs/${id}`);
  };

  // Get FAQs for a specific blog
  getBlogFaqs = async (blogId: string): Promise<IFaqResponse[]> => {
    const response = await this._apiSvc.get(
      `${appConstants.apiUrl}/faqs/blog/${blogId}/`
    );
    return response.data;
  };
}

export default BlogApi;
